class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (!customElements.get('trivino-cart-recommendations')) {
  customElements.define(
    'trivino-cart-recommendations',
    class TrivinoCartRecommendations extends HTMLElement {
      connectedCallback() {
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-trivino-mixed-completion]');
          if (!button || !this.contains(button)) return;
          event.preventDefault();
          this.addMixedSelection(button);
        });
        this.loadRecommendations();
      }

      setUnavailable() {
        const action = this.querySelector('[data-trivino-mixed-completion]');
        const summary = this.querySelector('[data-trivino-mixed-summary]');
        if (summary) summary.setAttribute('hidden', '');
        if (!action) return;

        action.disabled = true;
        action.removeAttribute('aria-busy');
        action.setAttribute('aria-label', this.dataset.unavailableLabel);
        const label = action.querySelector('span');
        if (label) label.textContent = this.dataset.unavailableLabel;
        this.classList.add('is-unavailable');
      }

      async loadRecommendations() {
        const action = this.querySelector('[data-trivino-mixed-completion]');
        const summary = this.querySelector('[data-trivino-mixed-summary]');
        const productId = this.dataset.productId;
        const referencePrice = Number.parseInt(this.dataset.referencePrice, 10);
        const remainingAmount = Number.parseInt(this.dataset.remainingAmount, 10);
        if (
          !action ||
          !summary ||
          !productId ||
          !Number.isInteger(referencePrice) ||
          !Number.isInteger(remainingAmount)
        ) {
          this.setUnavailable();
          return;
        }

        try {
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('section_id', 'cart-drawer-recommendations');
          url.searchParams.set('product_id', productId);
          url.searchParams.set('limit', '10');
          url.searchParams.set('intent', 'related');

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error('recommendations');

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = html.querySelector('[data-trivino-recommendations-results]');
          const excludedIds = new Set((this.dataset.cartProductIds || '').split(',').filter(Boolean));
          const candidates = [...(source?.querySelectorAll('[data-trivino-recommendation-card]') || [])]
            .filter((card) => {
              const price = Number.parseInt(card.dataset.price, 10);
              const minimum = Number.parseInt(card.dataset.quantityMinimum, 10) || 1;
              const inventoryLimit = Number.parseInt(card.dataset.inventoryLimit, 10);
              return (
                Number.isInteger(price) &&
                price > 0 &&
                minimum <= 1 &&
                (!Number.isInteger(inventoryLimit) || inventoryLimit >= 1) &&
                !excludedIds.has(card.dataset.productId)
              );
            })
            .sort((first, second) => {
              const firstDistance = Math.abs(Number.parseInt(first.dataset.price, 10) - referencePrice);
              const secondDistance = Math.abs(Number.parseInt(second.dataset.price, 10) - referencePrice);
              return firstDistance - secondDistance;
            });

          const toleranceSteps = [
            Math.max(Math.round(referencePrice * 0.3), 500),
            Math.max(Math.round(referencePrice * 0.45), 700),
            Math.max(Math.round(referencePrice * 0.6), 1000),
          ];
          let selectedCards = [];

          for (const tolerance of toleranceSteps) {
            const minimumPrice = referencePrice - tolerance;
            const maximumPrice = referencePrice + tolerance;
            const eligibleCards = candidates.filter((card) => {
              const price = Number.parseInt(card.dataset.price, 10);
              return price >= minimumPrice && price <= maximumPrice;
            });
            let selectedValue = 0;
            const selection = [];

            for (const card of eligibleCards) {
              if (selection.length >= 8) break;
              selection.push(card);
              selectedValue += Number.parseInt(card.dataset.price, 10);
              if (selectedValue >= remainingAmount) break;
            }

            if (selectedValue >= remainingAmount) {
              selectedCards = selection;
              break;
            }
          }

          if (selectedCards.length === 0) {
            this.setUnavailable();
            return;
          }

          const items = selectedCards.map((card) => ({
            id: Number.parseInt(card.dataset.variantId, 10),
            quantity: 1,
          }));
          if (items.some((item) => !Number.isInteger(item.id))) {
            this.setUnavailable();
            return;
          }

          const quantity = selectedCards.length;
          summary.textContent = this.dataset.mixedSummary.replace('__quantity__', String(quantity));
          action.dataset.items = JSON.stringify(items);
          action.querySelector('span').textContent = this.dataset.mixedLabel.replace('__quantity__', String(quantity));
          action.setAttribute('aria-label', this.dataset.mixedLabelAria.replace('__quantity__', String(quantity)));
          action.disabled = false;
          action.removeAttribute('aria-busy');
          summary.removeAttribute('hidden');
          this.classList.add('is-ready');
        } catch (error) {
          this.setUnavailable();
        }
      }

      async addMixedSelection(button) {
        let items;
        try {
          items = JSON.parse(button.dataset.items || '[]');
        } catch (error) {
          return;
        }
        if (!Array.isArray(items) || items.length === 0) return;

        const label = button.querySelector('span');
        const defaultLabel = label?.textContent;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        if (label) label.textContent = this.dataset.loadingLabel;

        try {
          const response = await fetch(routes.cart_add_url, {
            ...fetchConfig('javascript'),
            body: JSON.stringify({
              items,
              sections: ['cart-drawer', 'cart-icon-bubble'],
              sections_url: window.location.pathname,
            }),
          });
          const state = await response.json();
          if (!response.ok || state.status || state.errors) throw new Error('cart');

          const drawer = document.querySelector('cart-drawer');
          if (drawer) drawer.renderContents(state);
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'trivino-mixed-cart-completion',
            cartData: state,
          });
        } catch (error) {
          button.disabled = false;
          button.removeAttribute('aria-busy');
          if (label && defaultLabel) label.textContent = defaultLabel;
          const status = this.querySelector('[data-trivino-recommendations-status]');
          if (status) status.textContent = this.dataset.errorLabel;
        }
      }
    }
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
